import React from "react";
import { ClicksByCategory } from "../../../types/email-tracking.types";

interface ClicksByCategoryChartProps {
  data: ClicksByCategory[];
}

/**
 * Map category codes to Polish display labels
 */
const CATEGORY_LABELS: Record<string, string> = {
  call_details: "Szczegóły naboru",
  article_view: "Artykuł",
  external_article: "Artykuł zewnętrzny",
  official_url: "Oficjalne ogłoszenie",
  unsubscribe: "Rezygnacja",
  settings: "Ustawienia",
  email_open: "Otwarcie maila",
};

const formatCategoryLabel = (category: string): string => {
  return CATEGORY_LABELS[category] || category;
};

const formatPercent = (value: number, total: number): string => {
  if (total === 0) return "0%";
  return `${((value / total) * 100).toFixed(1)}%`;
};

export const ClicksByCategoryChart: React.FC<ClicksByCategoryChartProps> = ({ data }) => {
  const total = data.reduce((sum, item) => sum + item.count, 0);

  if (!data.length) {
    return (
      <div className="bg-white rounded-lg border border-neutral-200 p-4">
        <h4 className="text-sm font-semibold text-neutral-900 mb-3">
          Kliknięcia wg kategorii
        </h4>
        <div className="text-neutral-500 text-sm">Brak danych</div>
      </div>
    );
  }

  const sortedData = [...data].sort((a, b) => b.count - a.count);

  return (
    <div
      className="bg-white rounded-lg border border-neutral-200 p-4"
      data-testid="chart-clicks-by-category"
      role="region"
      aria-label="Wykres kliknięć wg kategorii"
    >
      <h4 className="text-sm font-semibold text-neutral-900 mb-3">
        Kliknięcia wg kategorii
      </h4>

      <div className="space-y-3">
        {sortedData.map((item, index) => {
          const percentage = total > 0 ? (item.count / total) * 100 : 0;

          return (
            <div key={item.category} data-testid={`category-item-${index}`}>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-neutral-700">
                  {formatCategoryLabel(item.category)}
                </span>
                <span className="text-neutral-900 font-medium">
                  {item.count.toLocaleString()}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-2 bg-neutral-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary-500 rounded-full transition-all"
                    style={{ width: `${percentage}%` }}
                    role="progressbar"
                    aria-valuenow={percentage}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={`${formatCategoryLabel(item.category)}: ${formatPercent(item.count, total)} kliknięć`}
                  />
                </div>
                <span className="text-xs text-neutral-500 w-12 text-right">
                  {formatPercent(item.count, total)}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 pt-3 border-t border-neutral-100">
        <div className="flex justify-between text-sm">
          <span className="text-neutral-600 font-medium">Suma</span>
          <span className="text-neutral-900 font-semibold">
            {total.toLocaleString()}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ClicksByCategoryChart;
